import { RequestOptions } from '@/services/device-http-proxy/types'

/**
 * Device Http Proxy Error centralizer.
 *
 * Use this error class to inform the top layers of the error that occurred.
 *
 * @param message - Human-readable and short description of the error.
 * @param code - Symbol representing the specific error type. Use one of the predefined symbols from `DeviceHttpProxyError.ERRORS`.
 * @param [errorPayload] - Optional additional data or context about the error. This can be used to provide more details for debugging or logging.
 */
export class DeviceHttpProxyError extends Error {
  static readonly ERRORS = {
    /**
     * Path was not found for CDM or LEDM protocol
     */
    PathNotFound: 'PathNotFound',

    /**
     * The firmware protocol is not being populated by OD Session
     */
    FirmwareProtocolNotFound: 'FirmwareProtocolNotFound',

    /**
     * Manifest resource type was not found in DeviceTree.xml
     */
    ManifestResourceTypeNotFound: 'ManifestResourceTypeNotFound',

    /**
     * Service was not found in DiscoveryTree.xml or DiscoveryServices.json
     */
    ServiceNotFound: 'ServiceNotFound',

    /**
     * Service not available for FW protocol requested
     */
    ServiceNotAvailable: 'ServiceNotAvailable',

    /**
     * DHP plugin is not available for use
     */
    PluginNotAvailable: 'PluginNotAvailable',

    /**
     * DHP request (sendRequest or sendRequestWithCacheStrategy issues) failed
     * with a {@link DeviceHttpProxyPluginResponseError} instance
     */
    RequestFailure: 'RequestFailure',

    /**
     * DHP request failed for some unknown reason
     */
    RequestUnknownFailure: 'RequestUnknownFailure',

    /**
     * DHP request returned non 2XX response status with a
     * {@link DeviceHttpProxyPluginHttpResponse} instance
     */
    RequestStatusFailure: 'RequestStatusFailure',
  } as const

  public requestPayload?: RequestOptions

  constructor(
    message: string,
    public readonly code: (typeof DeviceHttpProxyError.ERRORS)[keyof typeof DeviceHttpProxyError.ERRORS],
    public readonly errorPayload?: unknown,
  ) {
    super(`Device HTTP Proxy: ${message}`)
  }
}
